import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import useAlert from "../../hooks/Alert";
import AnimatedPage from "../FramerMotion/AnimatedPage";
import Alert from "./Alert/Alert";
import Title from "./misc/Title";

const formatCurrentDatetime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().replace("T", " ").split(".")[0];
    return encodeURIComponent(formattedDate);
};

function ForgotPasswordOTP({ baseURL, clientID, appSettings }) {
    const { showAlert, setShowAlert, alertStyle, setAlertStyle, alertMessage, setAlertMessage, hideAlert } = useAlert();
    const navigate = useNavigate();
    const [otp, setOtp] = useState("");
    const [timer, setTimer] = useState(60);
    const [resendDisabled, setResendDisabled] = useState(true);

    let userData = {};

    try {
        userData = JSON.parse(Cookies.get("wafaa-user-data-reset-key"));
    } catch (error) {
        navigate("/");
    }

    const isEmail = (data) => /\S+@\S+\.\S+/.test(data);
    const isValidMobile = (mobile) => mobile.startsWith(appSettings?.SMS_SETTINGS.ALLOWED_PREFIX);

    const sendOTP = async () => {
        if (!isEmail(userData) && !isValidMobile(userData)) {
            hideAlert();
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: `Invalid mobile number. Only numbers starting with ${appSettings?.SMS_SETTINGS.ALLOWED_PREFIX} are allowed.`,
                mode: "Forgot password",
            });
            setShowAlert("d-block");
            return;
        }

        const payLoad = {
            DIV_ID: "1",
            FUNCTION: "SendOTP",
            SEND_KEY: "123456",
            DATA: {
                [isEmail(userData) ? "EMAIL_ID" : "MOBILE_NUMBER"]: userData,
                TYPE_NAME: isEmail(userData) ? "EMAIL" : "MOBILE",
                CLIENT_ID: clientID,
            },
        };

        try {
            // Send OTP using Axios
            const response = await axiosInstance.post(`${baseURL}/SendOTP`, payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.data.SUCCESS === "false") {
                hideAlert();
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: response.data.MESSAGE,
                    mode: "Forgot password",
                });
                setShowAlert("d-block");
            } else {
                hideAlert();
                setAlertStyle({
                    background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                });
                setAlertMessage({
                    success: true,
                    message: "OTP sent successfully",
                    mode: "Forgot password",
                });
                setShowAlert("d-block");

                // Call SMS API if it's a valid mobile number
                if (!isEmail(userData)) {
                    const smsText = appSettings?.SMS_SETTINGS.OTP_MSG.replace("@OTP@", response.data.DATA.OTP);
                    const smsUrl = appSettings?.SMS_SETTINGS.API_URL;

                    try {
                        // Send SMS using Axios
                        const smsResponse = await axiosInstance.post(
                            smsUrl,
                            {
                                MobileNum: userData,
                                MessageText: smsText,
                            },
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                },
                            }
                        );

                        if (smsResponse.data?.data?.status === "SUCCESS") {
                            hideAlert();
                            setAlertStyle({
                                background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                            });
                            setAlertMessage({
                                success: true,
                                message: "SMS sent successfully!",
                                mode: "Forgot password",
                            });
                            setShowAlert("d-block");
                        } else {
                            throw new Error("Failed to send SMS");
                        }
                    } catch (smsError) {
                        hideAlert();
                        setAlertStyle({
                            background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                        });
                        setAlertMessage({
                            success: false,
                            message: smsError.response?.data?.message || "Failed to send SMS. Please retry.",
                            mode: "Forgot password",
                        });
                        setShowAlert("d-block");
                        throw smsError;
                    }
                } else {
                    // Send SMS using Axios
                    const mailResponse = await axiosInstance.post(`${baseURL}/LoyaltyEssOtpEmailSender`, {
                        TemplateFile: "email/otpmail.json",
                        mailTo: [
                            {
                                MailId: userData,
                                MailName: "",
                            },
                        ],
                        paramData: [
                            {
                                OTP: response.data?.DATA?.OTP,
                            },
                        ],
                    });

                    if (mailResponse.data?.SUCCESS) {
                        hideAlert();
                        setAlertStyle({
                            background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                        });
                        setAlertMessage({
                            success: true,
                            message: "Mail sent successfully!",
                            mode: "Forgot password",
                        });
                        setShowAlert("d-block");
                    } else {
                        throw new Error("Failed to send SMS. Please retry.");
                    }
                }
            }
        } catch (error) {
            hideAlert();
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: error.response?.data?.message || "An error occurred. Please try again.",
                mode: "Forgot password",
            });
            setShowAlert("d-block");
        }
    };

    const setAlert = (success, message, mode) => {
        hideAlert();
        setAlertStyle({
            background: success ? "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)" : "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
        });
        setAlertMessage({ success, message, mode });
        setShowAlert("d-block");
    };

    const verifyOTP = async () => {
        if (otp === "") {
            hideAlert();
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Blank OTP. Please input OTP",
                mode: "Verify OTP",
            });
            setShowAlert("d-block");
            return;
        }

        const payLoad = {
            DIV_ID: "1",
            FUNCTION: "VerifyOTP",
            SEND_KEY: "123456",
            DATA: {
                [isEmail(userData) ? "EMAIL_ID" : "MOBILE_NUMBER"]: userData,
                OTP: otp,
                CLIENT_ID: clientID,
            },
        };

        try {
            const response = await axiosInstance.post(`${baseURL}/VerifyOTP`, payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.data.SUCCESS === "false") {
                setAlert(false, response.data.MESSAGE, "Verify OTP");
            } else {
                setAlert(true, response.data.MESSAGE, "Verify OTP");
                navigate("/forgot-password-new-password");
            }
        } catch (error) {
            setAlert(false, error.response?.data?.message || "An error occurred during OTP verification", "Verify OTP");
        }
    };

    useEffect(() => {
        // Timer for enabling the resend button
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setResendDisabled(false);
        }
    }, [timer]);

    return (
        <AnimatedPage>
            <Title />
            <Alert showAlert={showAlert} alertStyle={alertStyle} alertMessage={alertMessage} hideAlert={hideAlert} />
            <div className="bg-white vh-100 d-flex flex-column justify-content-between px-4 py-5">
                <div>
                    <p className="font-size-18 font-weight-bolder text-center mt-5">OTP VERIFICATION</p>
                    <p className="mb-0 text-wafaa-grey font-size-12 text-center">
                        Enter the OTP sent to <span className="font-weight-bolder text-dark">- {userData}</span>
                    </p>
                    <div className="row m-0 p-0">
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            numInputs={4}
                            inputStyle="bg-wafaa-grey border-0 rounded-2 mt-4 ps-2 py-3 shadow-wafaa-btn-inset form-control text-center mx-2"
                            skipDefaultStyles={true}
                            renderInput={(props) => <input className="bg-wafaa-grey border-0 rounded-2 mt-4 ps-2 py-3 shadow-wafaa-btn-inset form-control text-center mx-2" {...props} />}
                        />
                    </div>
                    {timer !== 0 && <p className="text-center mt-4 text-wafaa-grey font-weight-bolder">{timer > 0 ? `Resend OTP in 00:${timer < 10 ? `0${timer}` : timer}` : ""}</p>}
                    {timer <= 0 && (
                        <p className="text-center p-3">
                            Didn't receive code?{" "}
                            <span
                                className={`font-weight-bolder ${resendDisabled ? "text-muted" : ""}`}
                                onClick={async () => {
                                    if (!resendDisabled) {
                                        setTimer(60);
                                        setResendDisabled(true);
                                        await sendOTP();
                                    }
                                }}
                            >
                                Re-send
                            </span>
                        </p>
                    )}
                </div>
            </div>
            <div className="position-absolute d-flex flex-column justify-content-end p-4 w-100 bottom-0">
                <button
                    type="button"
                    className="w-100 btn btn-lg py-2-5 btn-primary bg-wafaa-purple border-wafaa-purple shadow-wafaa-btn font-size-12"
                    onClick={() => {
                        verifyOTP();
                    }}
                >
                    Submit
                </button>
            </div>
        </AnimatedPage>
    );
}

export default ForgotPasswordOTP;
